import { Link } from "react-router-dom";
import {
  Truck,
  ShieldCheck,
  Package,
  CircleCheck,
  Sparkles,
  Layers,
  Utensils,
  Shirt,
  BookOpen,
  Boxes,
  Calendar,
  ArrowRight,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison & engagements logistiques ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Engagements logistiques
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Notre service de livraison de cartons {city.aCity} et nos
            engagements logistiques
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Parce que la préparation d&apos;un changement de domicile
            requiert une organisation rigoureuse, nous nous déplaçons
            directement à votre adresse pour vous ravitailler en matériel.
            Notre service de proximité repose sur des engagements clairs pour
            vous faciliter la vie.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {[
            {
              title: "Proximité et ponctualité",
              body: "Nous desservons avec réactivité tous les quartiers de Buc ainsi que les communes voisines comme Versailles, Guyancourt, Les Loges-en-Josas et Jouy-en-Josas.",
              Icon: Truck,
            },
            {
              title: "Zéro contrainte de transport",
              body: "Vos fournitures vous sont livrées à plat, parfaitement propres et sèches. Vous évitez ainsi de surcharger votre véhicule personnel avec des paquets encombrants et pouvez commencer à emballer vos pièces immédiatement.",
              Icon: ShieldCheck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="flex items-start gap-4 bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Cartons adaptés à chaque pièce ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Pièce par pièce
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Des cartons de déménagement adaptés à chaque pièce de votre
            logement
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour optimiser le chargement de votre camion et gagner un temps
            précieux lors du déballage, il est essentiel d&apos;utiliser le
            bon contenant pour le bon objet.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              title: "Pour la cuisine et le salon",
              body: "Nos cartons renforcés double cannelure protègent efficacement votre vaisselle, vos appareils électroménagers et vos objets de décoration fragiles.",
              Icon: Utensils,
            },
            {
              title: "Pour la chambre et le dressing",
              body: "Utilisez nos cartons penderie de grande hauteur dotés d'une tringle pour transférer vos vêtements sur cintres sans les froisser, ainsi que nos grands cartons pour le linge de lit volumineux.",
              Icon: Shirt,
            },
            {
              title: "Pour le bureau",
              body: "Nos petits cartons au format livre sont parfaits pour supporter le poids de vos bibliothèques ou de vos documents administratifs sans fatiguer votre dos lors de la manutention.",
              Icon: BookOpen,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-buc-cta-shop"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir tous les cartons par pièce
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Pourquoi qualité pro ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Qualité pro
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Pourquoi choisir des cartons de qualité professionnelle ?
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Investir dans du matériel haut de gamme conçu pour les
            déménageurs change radicalement le déroulement de votre journée
            de transfert.
          </p>
        </div>

        <div className="mt-8 space-y-4">
          {[
            {
              title: "Sécurité et sérénité",
              body: "Contrairement aux cartons de récupération souvent ramollis ou fragilisés par l'humidité, les modèles professionnels offrent une résistance supérieure à la charge et aux chocs.",
              Icon: ShieldCheck,
            },
            {
              title: "Optimisation de l'espace",
              body: "Leurs dimensions standardisées permettent de les empiler de façon stable et géométrique dans le véhicule de transport. Cela évite les glissements pendant les virages et maximise chaque mètre cube disponible.",
              Icon: Layers,
            },
            {
              title: "Facilité de repérage",
              body: "Chaque boîte dispose de grilles de marquage pré-imprimées pour vous permettre d'indiquer d'un coup de feutre la pièce de destination et son contenu exact.",
              Icon: Calendar,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 max-w-3xl bg-white border-l-4 border-[#237cb5] pl-5 py-2">
          <p className="text-[15px] md:text-base text-[#3a4856] leading-relaxed italic">
            En fin de compte, opter pour des fournitures de niveau
            professionnel est le meilleur calcul que vous puissiez faire
            pour vous épargner du stress inutile. C&apos;est l&apos;assurance
            de protéger ce que vous avez de plus précieux et de transformer
            une journée souvent redoutée en une transition fluide et
            parfaitement maîtrisée.{" "}
            <strong className="text-[#0f1d2b] not-italic">
              Ne prenez pas de risques avec vos souvenirs, misez sur la
              solidité dès le départ.
            </strong>
          </p>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-buc-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
